import { useFormContext } from 'react-hook-form';

import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';

import { fCurrency } from 'src/utils/format-number';

import { RHFTextField, RHFMaskTextField } from 'src/components/hook-form';

import { TRfmFormSchema } from '../rfm-form-schema';

type Props = {
  dimension: keyof TRfmFormSchema;
  enableForm: boolean;
};

const cutoffs = ['zoneOneMax', 'zoneTwoMax', 'zoneThreeMax', 'zoneFourMax'] as const;

export default function RfmBandFields({ dimension, enableForm }: Props) {
  const { watch } = useFormContext<TRfmFormSchema>();
  const bands = watch(dimension);
  const monetary = dimension === 'value';
  const unit = dimension === 'recency' ? 'dias' : 'compras';
  const formatValue = (value: number) => {
    if (monetary) return fCurrency(Number(value) / 100);
    const singularUnit = dimension === 'recency' ? 'dia' : 'compra';
    const suffix = Number(value) === 1 ? singularUnit : unit;
    return `${Number(value).toLocaleString('pt-BR', { maximumFractionDigits: 0 })} ${suffix}`;
  };

  return (
    <Stack
      spacing={2.5}
      sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, bgcolor: 'background.neutral' }}
    >
      {cutoffs.map((cutoff, index) => {
        const score = dimension === 'recency' ? 5 - index : index + 1;
        const previous = index ? bands[cutoffs[index - 1]] : null;
        const helperText =
          previous === null
            ? 'Inclui valores até este limite.'
            : `Acima de ${formatValue(previous)}, até este limite.`;

        return (
          <Stack key={cutoff} direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Typography variant="subtitle2" sx={{ minWidth: 96, pt: { sm: 1.5 } }}>
              Nota {score}
            </Typography>
            {monetary ? (
              <RHFMaskTextField
                name={`${dimension}.${cutoff}`}
                label={`Nota ${score}: até`}
                formatMask="MONEY"
                disabled={!enableForm}
                helperText={helperText}
              />
            ) : (
              <RHFTextField
                name={`${dimension}.${cutoff}`}
                label={`Nota ${score}: até`}
                disabled={!enableForm}
                inputProps={{ inputMode: 'numeric' }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{unit}</InputAdornment>,
                }}
                helperText={helperText}
              />
            )}
          </Stack>
        );
      })}
      <Divider />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <Typography variant="subtitle2" sx={{ minWidth: 96 }}>
          Nota {dimension === 'recency' ? 1 : 5}
        </Typography>
        <Typography variant="body2">
          Acima de {formatValue(bands.zoneFourMax)}, sem limite máximo.
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        Os limites devem crescer a cada faixa. Clientes sem compras válidas não recebem nota.
      </Typography>
    </Stack>
  );
}
